import React, { useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, BedDouble, Users } from "lucide-react";
import { toast } from "sonner";

const SUGGESTED = [
    "Which rooms are checking out today?",
    "Any VIP arrivals today?",
    "List all rooms needing cleaning by priority.",
    "What's the highest-severity open maintenance ticket?",
    "How many rooms are currently dirty?",
];

export default function Copilot() {
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState(null);
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);

    const ask = async (q) => {
        const query = q || question;
        if (!query.trim()) return;
        setBusy(true);
        setAnswer(null);
        try {
            const { data } = await api.post("/copilot/ask", { question: query });
            setAnswer(data);
            setHistory((h) => [{ q: query, a: data }, ...h].slice(0, 10));
            setQuestion("");
        } catch {
            toast.error("Copilot failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Grounded ops Q&A"
                title="Staff Copilot"
                subtitle="Ask questions about today's operations. Answers are grounded in your live data."
            />

            <Card className="mb-4">
                <CardContent className="py-4">
                    <div className="flex flex-wrap gap-2 mb-3">
                        {SUGGESTED.map((s) => (
                            <Button key={s} size="sm" variant="outline" onClick={() => ask(s)} disabled={busy} data-testid="copilot-suggest">
                                {s}
                            </Button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Textarea
                            placeholder="Ask anything about today…"
                            rows={2}
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    ask();
                                }
                            }}
                            data-testid="copilot-input"
                        />
                        <Button onClick={() => ask()} disabled={busy || !question.trim()} data-testid="copilot-ask">
                            <Sparkles className="w-4 h-4 mr-1" /> {busy ? "Thinking…" : "Ask"}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {answer && (
                <Card className="mb-4" data-testid="copilot-answer">
                    <CardContent className="py-4 space-y-3">
                        <div className="text-sm whitespace-pre-wrap">{answer.answer}</div>
                        <div className="flex flex-wrap gap-2">
                            {(answer.cited_rooms || []).map((rn) => (
                                <span key={rn} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs">
                                    <BedDouble className="w-3 h-3" /> Room {rn}
                                </span>
                            ))}
                            {(answer.cited_guests || []).map((g) => (
                                <span key={g} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs">
                                    <Users className="w-3 h-3" /> {g}
                                </span>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {history.length > 1 && (
                <Card>
                    <CardContent className="py-4 space-y-3">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">History</div>
                        {history.slice(1).map((h, i) => (
                            <div key={i} className="text-sm">
                                <div className="font-medium">Q: {h.q}</div>
                                <div className="text-muted-foreground whitespace-pre-wrap">A: {h.a.answer}</div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}
        </>
    );
}
